import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { faPlusSquare, faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';

import { Currency } from '@shared/components/helpers/currency';
import { Timestamp } from '@shared/components/helpers/timestamp';
import { Pagination } from '@shared/components/pagination/pagination';
import { IPagination } from '@shared/types/pagination';

import { Action as BillingReceiptAction } from '@admin/components/billing/receipts/action';
import { Action as BillingRefundAction } from '@admin/components/billing/refunds/action';
import { SourceIcon } from '@admin/components/billing/sources/source_icon';
import { titleize } from '@admin/components/helpers/titleize';
import { InvoiceSummary } from '@admin/components/invoice_summary';
import { Spinner } from '@admin/components/spinner';
import { Billing__Source } from '@admin/schema';

type SortColumn = 'date' | 'invoice_date';

interface NoteUser {
  name: string;
}

interface PaymentNote {
  id: number;
  body: string;
  user: NoteUser;
}

interface Source {
  id: string;
  funding_type: string;
  kind?: string;
  name?: string;
  brand?: string;
  number?: string;
  default?: boolean;
}

type SourceIconPayload = Pick<Billing__Source, 'id' | 'brand' | 'default' | 'fundingType' | 'number'>;

const sourceJsonForIcon = (raw: Source): SourceIconPayload => ({
  id: String(raw.id),
  brand: raw.brand,
  default: raw.default ?? false,
  fundingType: raw.funding_type as Billing__Source['fundingType'],
  number: raw.number ?? '',
});

interface ChargeTransaction {
  id: string;
  type: 'charge';
  date: string;
  amount: number;
  status: string;
  refunded: boolean;
  source: Source;
  failure_message?: string;
}

interface OfflinePaymentTransaction {
  id: string;
  type: 'offline_payment';
  date: string;
  amount: number;
  kind: string;
  transaction_number?: string;
  status: string;
}

interface RefundTransaction {
  id: string;
  type: 'refund';
  date: string;
  amount: number;
  status: string;
  source?: Source;
}

type InvoiceTransaction = ChargeTransaction | OfflinePaymentTransaction | RefundTransaction;

/** Lowercase ISO codes; matches `Currencies::CURRENCIES` on `Payment`. */
type PaymentCurrencyCode = 'usd' | 'cad';

interface InvoiceJson {
  id: number;
  date: string;
  total: number;
  summary: string;
  status: string;
  forgiven?: boolean;
  attempted?: boolean;
  paid_at?: string | null;
  currency: string;
  tax: number;
  pretax_subtotal: number;
  charges: ChargeTransaction[];
  offline_payments: OfflinePaymentTransaction[];
  refunds: RefundTransaction[];
  notes: PaymentNote[];
}

interface PaymentJson {
  id: number;
  receivable_id: string;
  kind: string;
  date: string;
  currency: PaymentCurrencyCode;
  amount: number;
  invoice: InvoiceJson;
  source_type: 'Source' | 'Offline';
  source?: Source;
  notes: PaymentNote[];
}

/** Shape of `pagination` from jbuilder `paginated` helper (snake_case). */
interface JbuilderPaginationJson {
  current_page: number;
  next_page?: number | null;
  prev_page?: number | null;
  total_pages: number;
  total_count: number;
}

/** Raw `/accounts/:id/payments.json` row; `currency` is normalized before use (see `paymentCurrencyFromApi`). */
type PaymentJsonApi = Omit<PaymentJson, 'currency'> & { currency: string };

interface PaymentsIndexResponse {
  results: PaymentJsonApi[];
  pagination: JbuilderPaginationJson;
}

/**
 * Narrows API `currency` to supported codes. Unknown values fall back to `usd` (`Currencies::DEFAULT_CURRENCY`).
 *
 * @param raw - `payment.currency` from JSON.
 * @returns A code accepted by {@link Currency}.
 */
const paymentCurrencyFromApi = (raw: unknown): PaymentCurrencyCode => {
  if (typeof raw !== 'string') {
    return 'usd';
  }
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'cad') {
    return 'cad';
  }
  return 'usd';
};

const jbuilderPaginationToShared = (p: JbuilderPaginationJson): IPagination => ({
  currentPage: p.current_page,
  nextPage: p.next_page ?? undefined,
  prevPage: p.prev_page ?? undefined,
  totalPages: p.total_pages,
  totalCount: p.total_count,
});

const invoiceStatusLabelClass = (state: string): string => {
  switch (state) {
    case 'refunded':
      return 'label-primary';
    case 'paid':
      return 'label-success';
    case 'failed':
      return 'label-warning';
    case 'final attempt failed':
      return 'label-danger';
    case 'not attempted':
      return 'label-info';
    default:
      return 'label-default';
  }
};

const transactionHistory = (invoice: InvoiceJson): InvoiceTransaction[] => {
  const rows: InvoiceTransaction[] = [...invoice.charges, ...invoice.offline_payments, ...invoice.refunds];
  return rows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

const lastChargeFailureMessage = (invoice: InvoiceJson): string | undefined => {
  const charges = invoice.charges;
  if (charges.length === 0) {
    return undefined;
  }
  return charges[charges.length - 1]?.failure_message;
};

interface PaymentInvoiceDetailsProps {
  payment: PaymentJson;
  open: boolean;
  /** Stable id for `aria-controls` on the row toggle (region stays in DOM, toggled with `hidden`). */
  contentId: string;
}

const PaymentInvoiceDetails: React.FC<PaymentInvoiceDetailsProps> = ({ payment, open, contentId }) => {
  const invoice = payment.invoice;
  const transactions = useMemo(() => transactionHistory(invoice), [invoice]);
  const failureMessage = lastChargeFailureMessage(invoice);

  return (
    <div id={contentId} className="accordian-div" hidden={!open}>
      <div className="row">
        <div className="col-md-6 text-left">
          <div>
            <b>Invoice Date:</b> <Timestamp value={invoice.date} format={Timestamp.Format.Date} />
          </div>
          <div>
            <b>Total:</b> <Currency value={invoice.total} />
          </div>
          <div>
            <b>Invoice #:</b> {invoice.id}
          </div>
          <div>
            <b>Status:</b>{' '}
            <span className={`label ${invoiceStatusLabelClass(invoice.status)}`}>
              <span>{titleize(invoice.status)}</span>
            </span>
            {invoice.forgiven && <span className="label label-primary">Forgiven</span>}
          </div>
          <br />
          <b>Description:</b>
          {invoice.summary}
          {invoice.attempted && !invoice.paid_at && failureMessage && (
            <div className="text-danger">
              <b>Error:</b> {failureMessage}
            </div>
          )}
          {invoice.notes.length > 0 && (
            <div>
              <b>Notes:</b>
              <ul>
                {invoice.notes.map((note) => (
                  <li key={note.id}>
                    {note.body} - <b>{note.user.name}</b>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {transactions.length > 0 && (
          <div className="col-md-6">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th className="col-sm-2">Type</th>
                  <th className="col-sm-2">Date</th>
                  <th className="col-sm-1">Amount</th>
                  <th className="col-sm-1" />
                  <th className="col-sm-3">Status</th>
                  <th className="col-sm-3">Method</th>
                  <th className="col-zero" />
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={`${transaction.type}-${transaction.id}`}>
                    <td className="col-sm-2">{titleize(transaction.type)}</td>
                    <td className="col-sm-2">
                      <Timestamp value={transaction.date} format={Timestamp.Format.Date} />
                    </td>
                    <td className="col-sm-1">
                      <Currency value={transaction.amount} />
                    </td>
                    <td className="col-sm-1" />
                    <td className="col-sm-3">{titleize(transaction.status)}</td>
                    <td className="col-sm-3">
                      {transaction.type === 'charge' && (
                        <SourceIcon
                          source={sourceJsonForIcon(transaction.source)}
                          fundingType={transaction.source.funding_type}
                          extraDetails
                          scale={2}
                        />
                      )}
                      {transaction.type === 'offline_payment' && (
                        <span>
                          {titleize(transaction.kind)} {transaction.transaction_number}
                        </span>
                      )}
                      {transaction.type === 'refund' && transaction.source && (
                        <SourceIcon
                          source={sourceJsonForIcon(transaction.source)}
                          fundingType={transaction.source.funding_type}
                          extraDetails
                          scale={2}
                        />
                      )}
                    </td>
                    <td className="col-zero col-nowrap">
                      {transaction.status !== 'failed' && (
                        <>
                          <BillingReceiptAction
                            transaction={{
                              id: transaction.id,
                              type: transaction.type,
                              amount: transaction.amount,
                              date: transaction.date,
                            }}
                          />
                          {transaction.type === 'charge' && (
                            <a
                              target="_blank"
                              href={`/stripe/charges/${transaction.id}`}
                              className="btn btn-default"
                              rel="noreferrer"
                            >
                              View
                            </a>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

interface PaymentRowProps {
  payment: PaymentJson;
  open: boolean;
  onToggle: () => void;
}

const PaymentRow: React.FC<PaymentRowProps> = ({ payment, open, onToggle }) => {
  const detailsContentId = `payment-invoice-details-${payment.id}`;
  return (
    <>
      <tr>
        <td className="col-md-1 col-xs-1 text-left link">
          <button
            type="button"
            className="btn btn-link nuke-horizontal-padding"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={detailsContentId}
            aria-label={`${open ? 'Collapse' : 'Expand'} details for invoice ${payment.invoice.id}`}
          >
            <FontAwesomeIcon id={`payment-accordian-icon-${payment.id}`} icon={open ? faMinusSquare : faPlusSquare} />
            {payment.invoice.id}
          </button>
        </td>
        <td className="col-md-2 col-xs-2 text-left">
          <Timestamp value={payment.date} format={Timestamp.Format.Date} />
        </td>
        <td className="col-md-1 col-xs-1 text-left">{payment.kind}</td>
        <td className="col-md-3 col-xs-4 text-left">
          <InvoiceSummary invoice={payment.invoice} />
        </td>
        <td className="text-left col-md-2 hidden-xs">
          {payment.notes.map((note) => (
            <div key={note.id}>
              {note.body} <b>{note.user.name}</b>
            </div>
          ))}
        </td>
        <td className="col-md-1 col-xs-1 text-right">
          <Currency value={payment.amount} currency={payment.currency} />
        </td>
        <td className="col-md-1 col-xs-1 text-left">
          {payment.kind === 'Charge' && (
            <BillingRefundAction
              refundable={{
                id: String(payment.id),
                receivable_id: String(payment.receivable_id),
                amount: payment.amount,
              }}
            />
          )}
        </td>
        <td className="col-md-1 hidden-xs">
          {payment.source_type === 'Source' && payment.source ? (
            <SourceIcon
              source={sourceJsonForIcon(payment.source)}
              fundingType={payment.source.funding_type}
              extraDetails
              scale={2}
            />
          ) : (
            <span>{titleize(payment.source_type)}</span>
          )}
        </td>
        <td className="col-md-2 col-xs-2">
          <Timestamp value={payment.invoice.date} format={Timestamp.Format.Date} />
        </td>
      </tr>
      <tr className="accordian-tr">
        <td colSpan={12} className="nuke-padding">
          <PaymentInvoiceDetails contentId={detailsContentId} payment={payment} open={open} />
        </td>
      </tr>
    </>
  );
};

export interface AccountPaymentsTableProps {
  accountID: string | number;
}

/**
 * Credit card charges and refunds for an account (admin account Billing tab).
 *
 * @param props.accountID - Account id from the account show page.
 */
export const AccountPaymentsTable: React.FC<AccountPaymentsTableProps> = ({ accountID }) => {
  const [sort, setSort] = useState<SortColumn>('date');
  const [pagination, setPagination] = useState<IPagination | null>(null);
  const [payments, setPayments] = useState<PaymentJson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openById, setOpenById] = useState<Record<number, boolean>>({});
  const lastRequestIdRef = useRef(0);
  const tableRootRef = useRef<HTMLDivElement>(null);
  /** Set only when the user changes page via pagination (not initial load or sort). */
  const pendingPaymentsPaginationScrollRef = useRef(false);

  const load = useCallback(
    async (page: number, sortColumn: SortColumn) => {
      const requestId = ++lastRequestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get<PaymentsIndexResponse>(`/accounts/${accountID}/payments.json`, {
          params: { page, sort: sortColumn },
        });
        if (requestId !== lastRequestIdRef.current) {
          return;
        }
        setPayments(
          data.results.map((row) => ({
            ...row,
            currency: paymentCurrencyFromApi(row.currency),
          })),
        );
        setPagination(jbuilderPaginationToShared(data.pagination));
      } catch (e) {
        if (requestId !== lastRequestIdRef.current) {
          return;
        }
        setError('Could not load payments.');
        setPayments([]);
        setPagination(null);
      } finally {
        if (requestId === lastRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [accountID],
  );

  useEffect(() => {
    void load(1, sort);
  }, [load, sort]);

  useLayoutEffect(() => {
    if (!loading && pendingPaymentsPaginationScrollRef.current) {
      pendingPaymentsPaginationScrollRef.current = false;
      tableRootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [loading]);

  const onPage = (page: number) => {
    pendingPaymentsPaginationScrollRef.current = true;
    tableRootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    void load(page, sort);
  };

  const resort = (column: SortColumn) => {
    setSort(column);
  };

  const toggle = (id: number) => {
    setOpenById((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading && payments.length === 0 && !pagination) {
    return <Spinner />;
  }

  if (error) {
    return <div className="text-danger">{error}</div>;
  }

  return (
    <div ref={tableRootRef} className="account-payments-table">
      <table className="table">
        <thead>
          <tr>
            <th className="col-md-1 col-xs-1">
              <div className="btn nuke-horizontal-padding">Invoice</div>
            </th>
            <th className="col-md-1 col-xs-2 text-left" aria-sort={sort === 'date' ? 'ascending' : 'none'}>
              <button
                type="button"
                className="btn nuke-horizontal-padding"
                onClick={() => resort('date')}
                aria-pressed={sort === 'date'}
                aria-label={sort === 'date' ? 'Date, sorted ascending' : 'Sort by date'}
              >
                Date <span className={sort === 'date' ? 'caret text-primary' : 'caret'} />
              </button>
            </th>
            <th className="col-md-1 col-xs-1 text-left">
              <div className="btn nuke-horizontal-padding">Type</div>
            </th>
            <th className="col-md-3 col-xs-4 text-left">
              <div className="btn nuke-horizontal-padding">Description</div>
            </th>
            <th className="col-md-1 col-xs-1 text-right">
              <div className="btn nuke-horizontal-padding">Notes</div>
            </th>
            <th className="col-md-1 col-xs-1 text-right">
              <div className="btn nuke-horizontal-padding">Amount</div>
            </th>
            <th className="col-md-1 col-xs-1 text-left" />
            <th className="col-md-1 hidden-xs">
              <div className="btn nuke-horizontal-padding">Method</div>
            </th>
            <th className="col-md-2 col-xs-2" aria-sort={sort === 'invoice_date' ? 'ascending' : 'none'}>
              <button
                type="button"
                className="btn nuke-horizontal-padding"
                onClick={() => resort('invoice_date')}
                aria-pressed={sort === 'invoice_date'}
                aria-label={sort === 'invoice_date' ? 'Invoice date, sorted ascending' : 'Sort by invoice date'}
              >
                Invoice Date <span className={sort === 'invoice_date' ? 'caret text-primary' : 'caret'} />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              open={!!openById[payment.id]}
              onToggle={() => toggle(payment.id)}
            />
          ))}
        </tbody>
      </table>
      {pagination && <Pagination pagination={pagination} onPage={onPage} />}
    </div>
  );
};
